import { motion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { services } from '../../../content/servicesCopy';
import ServiceDemo from './ServiceDemo';
import { StatusPill, ToolChips } from './ServiceParts';

/*
 * Systems — one module per screen.
 *
 * Deliberately does NOT use CSS scroll-snap. A snap container on a marketing
 * page has to be its own scroller, which traps trackpad users in a nested
 * scroll area and breaks the browser's own scroll position restore. Cards are
 * simply tall (88vh) in normal document flow, which gets the same
 * one-at-a-time effect with none of the hijacking.
 */

const CARD_MIN_VH = 88;

function FullCard({ c, index }) {
  const demoFirst = index % 2 === 1;

  return (
    <section
      className="flex flex-col justify-center py-16"
      style={{ minHeight: `${CARD_MIN_VH}vh` }}
      aria-labelledby={`sys-${c.key}-title`}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        className="fa-plate fa-plate--interactive fa-ticks p-6 md:p-12"
      >
        <div className="grid gap-10 md:gap-16 md:grid-cols-12 items-center">
          <div className={`md:col-span-6 ${demoFirst ? 'md:order-2' : ''}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="fa-ref fa-ref--signal" style={{ fontSize: 12 }}>{c.ref}</span>
              <StatusPill status={c.status} size="lg" />
            </div>

            <h3
              id={`sys-${c.key}-title`}
              className="font-heading text-[28px] md:text-[40px] font-semibold mt-6 leading-[1.12]"
              style={{ color: 'var(--fa-ink)', maxWidth: '18ch', textWrap: 'balance' }}
            >
              {c.title}
            </h3>

            <p
              className="mt-6 text-[16px] md:text-[17px] fa-glow-text fa-glow-text--muted"
              style={{ maxWidth: '52ch' }}
            >
              {c.body}
            </p>

            <p
              className="fa-leader mt-7"
              style={{
                color: 'var(--fa-ink)',
                textTransform: 'none',
                letterSpacing: '0.01em',
                fontSize: '15px',
                maxWidth: '46ch',
              }}
            >
              {c.outcome}
            </p>

            <div className="mt-8 pt-7" style={{ borderTop: '1px solid var(--fa-rule)' }}>
              <ToolChips tools={c.tools} size="lg" />
            </div>

            <div className="mt-7 pt-6" style={{ borderTop: '1px solid var(--fa-rule)' }}>
              <span className="fa-label" style={{ color: 'var(--fa-muted)' }}>Best for</span>
              <p className="mt-2 text-[14px]" style={{ color: 'var(--fa-ink)' }}>{c.bestFor}</p>
            </div>

            {c.examples?.length > 0 && (
              <details className="fa-examples mt-7 pt-6" style={{ borderTop: '1px solid var(--fa-rule)' }}>
                <summary
                  className="flex items-center justify-between cursor-pointer list-none fa-label"
                  style={{ color: 'var(--fa-ink)' }}
                >
                  <span>What it covers</span>
                  <ChevronDown
                    size={15}
                    className="fa-examples-chev transition-transform"
                    style={{ color: 'var(--fa-signal)' }}
                  />
                </summary>
                <ul className="mt-4 space-y-3">
                  {c.examples.map((ex, i) => (
                    <li key={i} className="text-[14px] flex items-start gap-3 fa-glow-text fa-glow-text--muted">
                      <span className="fa-mono mt-0.5" style={{ color: 'var(--fa-signal)', fontSize: 10 }} aria-hidden="true">
                        →
                      </span>
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className={`md:col-span-6 ${demoFirst ? 'md:order-1' : ''}`}>
            <ServiceDemo card={c} large />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      className="fa-grid px-6 pt-24 pb-16"
      style={{ background: 'var(--fa-paper)', borderTop: '1px solid var(--fa-rule)' }}
    >
      <div className="max-w-[1120px] mx-auto">
        <div className="fa-label fa-header-rule mb-5">
          <span className="flex items-center gap-3">
            <span className="fa-status-dot fa-status-dot--live" aria-hidden="true" />
            {services.label}
          </span>
        </div>
        <h2
          className="font-heading text-[30px] md:text-[42px] font-semibold"
          style={{ maxWidth: '22ch', color: 'var(--fa-ink)', textWrap: 'balance' }}
        >
          {services.headline}
        </h2>
        <p className="mt-5 text-[16px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '58ch' }}>
          {services.intro}
        </p>
        <p
          className="fa-leader mt-6"
          style={{
            color: 'var(--fa-ink)',
            textTransform: 'none',
            letterSpacing: '0.01em',
            fontSize: '14px',
            maxWidth: '56ch',
          }}
        >
          {services.customNote}
        </p>
      </div>

      <div className="max-w-[1120px] mx-auto mt-10">
        {services.cards.map((c, i) => (
          <FullCard key={c.key} c={c} index={i} />
        ))}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-4 mb-8 flex flex-col sm:flex-row gap-4 sm:gap-8 px-6 py-5"
          style={{ borderLeft: '2px solid var(--fa-signal)', background: 'var(--fa-surface)', borderRadius: 5 }}
        >
          <span className="fa-ref fa-ref--signal flex-shrink-0 pt-0.5">{services.dogfood.label}</span>
          <p className="text-[14.5px] fa-glow-text" style={{ maxWidth: '68ch' }}>
            {services.dogfood.body}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
